type HealthStatus = "ok" | "warn" | "fail";

export type RuntimeHealthCheck = {
  name: string;
  status: HealthStatus;
  detail: string;
  durationMs: number;
};

const withTimeout = async (url: string, timeoutMs = 5000) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  const started = performance.now();
  try {
    const response = await fetch(url, { cache: "no-store", signal: controller.signal });
    return {
      status: response.ok ? "ok" as const : "warn" as const,
      detail: `${response.status} ${response.statusText}`.trim(),
      durationMs: Math.round(performance.now() - started),
    };
  } catch (error) {
    return {
      status: "fail" as const,
      detail: error instanceof Error ? error.message : String(error),
      durationMs: Math.round(performance.now() - started),
    };
  } finally {
    window.clearTimeout(timer);
  }
};

export const runRuntimeHealthChecks = async (): Promise<RuntimeHealthCheck[]> => {
  const origin = window.location.origin;
  const checks = [
    ["document", `${origin}/?health=${Date.now()}`],
    ["main module", `${origin}/src/main.tsx?health=${Date.now()}`],
    ["robots", `${origin}/robots.txt?health=${Date.now()}`],
    ["backend auth", `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/health`],
  ] as const;

  return Promise.all(checks.map(async ([name, url]) => ({ name, ...(await withTimeout(url)) })));
};

export const renderSafeLanding = (reason = "bundle-rollback") => {
  const root = document.getElementById("root");
  if (!root) return;
  document.documentElement.classList.remove("dark");
  root.innerHTML = `
    <main data-bantoo-safe-landing="${reason}" style="min-height:100vh;background:#F5F7FA;color:#0F172A;font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
      <nav style="height:64px;display:flex;align-items:center;justify-content:space-between;padding:0 24px;border-bottom:1px solid #E2E8F0;box-sizing:border-box;">
        <strong style="font-size:20px;font-family:'Space Grotesk',sans-serif;">BantooMoney™</strong>
        <span><a href="/login" style="color:#0F172A;margin-right:16px;">Sign in</a><a href="/signup" style="background:#F59E0B;color:#0F172A;text-decoration:none;padding:10px 16px;border-radius:8px;font-weight:700;">Create account</a></span>
      </nav>
      <section style="max-width:960px;margin:0 auto;padding:88px 24px 48px;text-align:center;box-sizing:border-box;">
        <p style="margin:0 0 12px;color:#047857;font-weight:700;">Recovery mode active</p>
        <h1 style="margin:0 auto 20px;max-width:780px;font:700 48px/1.08 'Space Grotesk',sans-serif;color:#0F172A;">Africa & diaspora money, restored safely.</h1>
        <p style="margin:0 auto 32px;max-width:680px;font-size:18px;line-height:1.7;color:#334155;">A protected fallback is showing while your browser refreshes the latest app bundle. You can still sign in, create an account, or retry the full experience.</p>
        <a href="/?bantoo_force_app=1" style="display:inline-block;background:#F59E0B;color:#0F172A;text-decoration:none;padding:14px 22px;border-radius:8px;font-weight:800;margin-right:12px;">Retry full app</a>
        <a href="/login" style="display:inline-block;color:#0F172A;padding:14px 16px;font-weight:700;">Continue to login</a>
      </section>
    </main>`;
};

export const installRuntimeLogViewer = () => {
  if (document.getElementById("bantoo-runtime-log-viewer")) return;

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Logs";
  button.setAttribute("aria-label", "Open runtime diagnostics log viewer");
  button.style.cssText = "position:fixed;left:12px;bottom:12px;z-index:2147483647;background:#0F172A;color:#F8FAFC;border:1px solid #334155;border-radius:8px;padding:8px 10px;font:700 12px system-ui;box-shadow:0 8px 24px rgba(15,23,42,.24);";

  const panel = document.createElement("section");
  panel.id = "bantoo-runtime-log-viewer";
  panel.style.cssText = "display:none;position:fixed;left:12px;bottom:52px;z-index:2147483647;width:min(680px,calc(100vw - 24px));max-height:min(520px,calc(100vh - 80px));overflow:auto;background:#0F172A;color:#E2E8F0;border:1px solid #334155;border-radius:8px;padding:14px;font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;box-shadow:0 20px 50px rgba(15,23,42,.35);white-space:pre-wrap;";

  const render = () => {
    const errors = (window as typeof window & { __BANTOO_BOOT_ERRORS__?: unknown[] }).__BANTOO_BOOT_ERRORS__ || [];
    panel.textContent = JSON.stringify({
      path: window.location.href,
      readyState: document.readyState,
      reactMounted: document.getElementById("root")?.getAttribute("data-bantoo-react-mounted") === "true",
      errors,
    }, null, 2);
  };

  button.onclick = () => {
    render();
    panel.style.display = panel.style.display === "none" ? "block" : "none";
  };

  document.body.append(panel, button);
};

export const exposeRuntimeHealthChecks = () => {
  (window as typeof window & { __BANTOO_RUN_HEALTH_CHECKS__?: () => Promise<RuntimeHealthCheck[]> }).__BANTOO_RUN_HEALTH_CHECKS__ = runRuntimeHealthChecks;
};